import { useTranslation } from 'react-i18next';
import { EditOutlined, HomeOutlined, MinusOutlined, UserOutlined } from '@ant-design/icons';
import { useCallback, useMemo } from 'react';
import { Col, Divider, Space, Typography } from 'antd';
import { FieldArray, useFormikContext } from 'formik';
import { Customer } from 'store/customers/customer';
import Button from 'components/new-ui/base/button/Button';
import { invalidEoriMessage } from 'views/declarations/utils/validation-utils';
import { Eori } from 'store/eori/eori';
import FormEori from 'components/ui/composed/formEori/FormEori';
import DeclarationInput from 'components/ui/composed/declarations/formInput';
import CountrySelect from 'components/ui/composed/declarations/CountrySelect';
import FormTextArea from 'components/new-ui/composed/formTextArea/FormTextArea';
import {
    CustomerAddressRow,
    CustomerContactContainer,
    CustomerContactHeaderContainer,
    CustomerContactHeaderText,
} from '../styles';
import FormPhoneNumber from 'components/new-ui/composed/formPhoneNumber/FormPhoneNumber';
import HeaderWithIcon from 'components/new-ui/composed/header/HeaderWithIcon';

interface Props {
    active: boolean;
}

const GeneralStep = ({ active }: Props) => {
    const { t } = useTranslation('customers');
    const { getFieldProps, getFieldMeta, getFieldHelpers, setFieldValue, setFieldError, values } =
        useFormikContext<Partial<Customer>>();

    const fillEoriInfo = useCallback(
        (eori: Eori) => {
            setFieldValue('name', eori?.name);
            setFieldValue('eori', eori?.eori);
            setFieldValue('address.addressLine1', eori?.street);
            setFieldValue('address.city', eori?.city);
            setFieldValue('address.postCode', eori?.postCode);
        },
        [setFieldValue]
    );

    const onError = useCallback(() => {
        setFieldError('eori', invalidEoriMessage);
    }, [setFieldError]);

    const onSuccess = useCallback(() => {
        setFieldError('eori', undefined);
    }, [setFieldError]);

    const hasValues = useMemo(
        () =>
            !!getFieldProps('name').value ||
            !!getFieldProps('address.addressLine1').value ||
            !!getFieldProps('address.city').value ||
            !!getFieldProps('address.postCode').value,
        [getFieldProps]
    );

    return (
        <Space direction="vertical" size={16} style={{ width: '100%', display: active ? 'flex' : 'none' }}>
            <HeaderWithIcon
                title="Party Details"
                subtitle={<Typography.Text>Complete all required fields to create a party</Typography.Text>}
                iconProps={{ icon: <HomeOutlined />, type: 'warning' }}
                style={{ marginTop: 16 }}
            />

            <DeclarationInput
                label="Party name"
                fieldMeta={getFieldMeta('name')}
                fieldProps={getFieldProps('name')}
                fieldHelper={getFieldHelpers('name')}
                condensed
                required
                labelSpacing="medium"
            />

            <DeclarationInput
                label="Reference Code"
                fieldMeta={getFieldMeta('referenceCode')}
                fieldProps={getFieldProps('referenceCode')}
                fieldHelper={getFieldHelpers('referenceCode')}
                tooltip={t('common.reference_code_info')}
                condensed
                required
                labelSpacing="medium"
            />

            <FormEori
                declaration
                hasValues={hasValues}
                fieldMeta={getFieldMeta('eori')}
                fieldProps={getFieldProps('eori')}
                fieldHelper={getFieldHelpers('eori')}
                onApply={fillEoriInfo}
                onError={onError}
                onSuccess={onSuccess}
                tooltip={t('common.eori_tooltip')}
                condensed
                required
                labelSpacing="medium"
            />

            <DeclarationInput
                required
                condensed
                labelSpacing="medium"
                label="Address Line 1"
                fieldMeta={getFieldMeta('address.addressLine1')}
                fieldProps={getFieldProps('address.addressLine1')}
                fieldHelper={getFieldHelpers('address.addressLine1')}
            />

            <DeclarationInput
                condensed
                labelSpacing="medium"
                label="Address Line 2"
                fieldMeta={getFieldMeta('address.addressLine2')}
                fieldProps={getFieldProps('address.addressLine2')}
                fieldHelper={getFieldHelpers('address.addressLine2')}
            />

            <CustomerAddressRow gutter={8}>
                <Col span={16}>
                    <DeclarationInput
                        condensed
                        required
                        labelSpacing="medium"
                        label="City"
                        fieldMeta={getFieldMeta('address.city')}
                        fieldProps={getFieldProps('address.city')}
                        fieldHelper={getFieldHelpers('address.city')}
                    />
                </Col>

                <Col span={8}>
                    <DeclarationInput
                        condensed
                        required
                        labelSpacing="medium"
                        placeholder="Post Code"
                        label="Post Code"
                        fieldMeta={getFieldMeta('address.postCode')}
                        fieldProps={getFieldProps('address.postCode')}
                        fieldHelper={getFieldHelpers('address.postCode')}
                    />
                </Col>
            </CustomerAddressRow>

            <CountrySelect
                hideKeys
                condensed
                labelSpacing="medium"
                required
                label="Country"
                fieldMeta={getFieldMeta('address.country')}
                fieldProps={getFieldProps('address.country')}
                fieldHelper={getFieldHelpers('address.country')}
            />

            <Divider style={{ padding: 0, margin: 0 }} />

            <HeaderWithIcon
                title="Contact Details"
                subtitle={<Typography.Text>An email and/or Phone number is required</Typography.Text>}
                iconProps={{ icon: <UserOutlined />, type: 'success' }}
                style={{ marginTop: 16 }}
            />

            <DeclarationInput
                required
                condensed
                labelSpacing="medium"
                label="Contact Name"
                fieldMeta={getFieldMeta('contact.name')}
                fieldProps={getFieldProps('contact.name')}
                fieldHelper={getFieldHelpers('contact.name')}
            />

            <DeclarationInput
                required
                condensed
                labelSpacing="medium"
                label="Email Addresss"
                fieldMeta={getFieldMeta('contact.email')}
                fieldProps={getFieldProps('contact.email')}
                fieldHelper={getFieldHelpers('contact.email')}
            />

            <FormPhoneNumber
                alignLabel
                phoneNumberFieldProps={getFieldProps('contact.phoneNumber')}
                phoneNumberFieldMeta={getFieldMeta('contact.phoneNumber')}
                phoneNumberFieldHelpers={getFieldHelpers('contact.phoneNumber')}
                codeFieldProps={getFieldProps('contact.phoneCountryCode')}
                codeFieldMeta={getFieldMeta('contact.phoneCountryCode')}
                codeFieldHelpers={getFieldHelpers('contact.phoneCountryCode')}
            />

            {/* Note: Currently the BE does not handle the additional contacts */}
            {/* This will be fixed in the BE in the future and might need an update also here */}
            {/* Need to test that we display them once we have them in the payload */}
            <FieldArray
                name="contacts"
                render={(arrayHelpers) => {
                    return (
                        <div>
                            {values.contacts?.map((_, index) => (
                                <>
                                    <CustomerContactContainer>
                                        <CustomerContactHeaderContainer>
                                            <CustomerContactHeaderText>
                                                Additional Contact {index + 1}
                                            </CustomerContactHeaderText>
                                            <Divider style={{ width: 'auto', minWidth: 0, flex: '1' }} />
                                            <Button
                                                icon={<MinusOutlined />}
                                                onClick={() => arrayHelpers.remove(index)}
                                            />
                                        </CustomerContactHeaderContainer>

                                        <Space direction="vertical" size={16}>
                                            <DeclarationInput
                                                required
                                                condensed
                                                labelSpacing="medium"
                                                label="Contact Name"
                                                fieldMeta={getFieldMeta(`contacts.${index}.name`)}
                                                fieldProps={getFieldProps(`contacts.${index}.name`)}
                                                fieldHelper={getFieldHelpers(`contacts.${index}.name`)}
                                            />

                                            <DeclarationInput
                                                required
                                                condensed
                                                labelSpacing="medium"
                                                label="Email Addresss"
                                                fieldMeta={getFieldMeta(`contacts.${index}.email`)}
                                                fieldProps={getFieldProps(`contacts.${index}.email`)}
                                                fieldHelper={getFieldHelpers(`contacts.${index}.email`)}
                                            />

                                            <FormPhoneNumber
                                                alignLabel
                                                codeFieldHelpers={getFieldHelpers(`contacts.${index}.phoneCountryCode`)}
                                                codeFieldProps={getFieldProps(`contacts.${index}.phoneCountryCode`)}
                                                codeFieldMeta={getFieldMeta(`contacts.${index}.phoneCountryCode`)}
                                                phoneNumberFieldProps={getFieldProps(`contacts.${index}.phoneNumber`)}
                                                phoneNumberFieldMeta={getFieldMeta(`contacts.${index}.phoneNumber`)}
                                                phoneNumberFieldHelpers={getFieldHelpers(
                                                    `contacts.${index}.phoneNumber`
                                                )}
                                            />
                                        </Space>
                                    </CustomerContactContainer>
                                </>
                            ))}
                        </div>
                    );
                }}
            />

            <Divider style={{ padding: 0, margin: 0 }} />

            <HeaderWithIcon
                title="Special Instructions"
                subtitle={<Typography.Text>Add any additional information or requirements</Typography.Text>}
                iconProps={{ icon: <EditOutlined />, type: 'note' }}
                style={{ marginTop: 16 }}
            />

            <FormTextArea
                id="specialInstructions"
                placeholder="Enter special instructions"
                fieldMeta={getFieldMeta('specialInstructions')}
                fieldProps={getFieldProps('specialInstructions')}
                fieldHelper={getFieldHelpers('specialInstructions')}
            />
        </Space>
    );
};

export default GeneralStep;
